import { React, moment } from 'Imports';
import { useEffect, useMemo, useState } from 'react';
import SliderDialog from '$Components/Shared/MaterialUIComponents/SliderDialog';
import { CameraDeviceSegment, VehicleCameraPairingResponse } from '$Generated/api';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { isEncompass } from '$Utilities/integrationPlatform';
import { SelectCameraStep } from './SelectCameraStep';
import { SelectDateStep } from './SelectDateStep/SelectDateStep';
import { TimeAndDurationStep } from './TimeAndDurationStep/TimeAndDurationStep';
import { SegmentPreviewStep } from './SegmentPreviewStep/SegmentPreviewStep';
import { CameraSegmentBase, CameraSegmentFullData, FootageViewState } from './TimeAndDurationStep/FootageSegments';
import { FleetSettingsService, IFleetSettingsServiceInjectedProps } from '$State/FleetSettingsFreezerService';
import { FleetSettingsKeys } from '$Utilities/Enums/FleetSettingsKeys';
import { IVideoRecallServiceInjectedProps, VideoRecallService } from '$State/VideoRecallFreezerService';
import { useAppSnackbar } from '$Utilities/hooks/useAppSnackbar';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';

const styles = require('./RequestVideoDrawer.scss') as {
    drawerPaper: string;
    drawerPaperWide: string;
    drawerHeader: string;
    drawerFooter: string;
    contentWrapper: string;
    footerButtons: string;
    cancelButton: string;
    nextButton: string;
    content: string;
    searchInputContainer: string;
    searchInput: string;
    searchIcon: string;
    note: string;
    noteLabel: string;
    titleDescriptionWrapper: string;
    verbiage: string;
    cameraList: string;
    cameraTile: string;
    cameraTileSelected: string;
    cameraInfo: string;
    cameraSerial: string;
    vehicleName: string;
    statusIndicator: string;
    onlineIndicator: string;
    offlineIndicator: string;
    onlineText: string;
    offlineText: string;
    noResults: string;
    onlineToggleText: string;
};

const DEFAULT_DURATION_SECONDS = 120;

function formatDuration(seconds: number): string {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    const minLabel = `${min} minute${min === 1 ? '' : 's'}`;
    const secLabel = `${sec} second${sec === 1 ? '' : 's'}`;
    if (min === 0) return secLabel;
    if (sec === 0) return minLabel;
    return `${minLabel} ${secLabel}`;
}

function buildDurationOptions(maxSeconds: number): IDropdownItem[] {
    const options: IDropdownItem[] = [];
    const fixedSteps = [30, 60, 90, 120];
    for (const s of fixedSteps) {
        if (s <= maxSeconds) {
            options.push({ label: formatDuration(s), value: s });
        }
    }
    for (let s = 180; s <= maxSeconds; s += 60) {
        options.push({ label: formatDuration(s), value: s });
    }
    return options;
}

interface IRequestVideoDrawerProps {
    open: boolean;
    onClose: () => void;
    onRequestSubmitted?: () => void | Promise<void>;
    pairings: VehicleCameraPairingResponse[];
    config: any;
    // When provided, the camera/vehicle is already known (e.g. opened from a specific device's row
    // elsewhere) and the drawer skips straight to date selection instead of asking the user to pick one.
    preselectedPairingId?: number;
}

type Step = 'selectCamera' | 'selectDate' | 'timeAndDuration' | 'segmentPreview';
type StartTime = { hour: number; minute: number; second: number; amPm: 'AM' | 'PM' };

type RequestVideoDrawerProps = IRequestVideoDrawerProps & IFleetSettingsServiceInjectedProps & IVideoRecallServiceInjectedProps;

function _RequestVideoDrawer(props: RequestVideoDrawerProps) {
    const { showSnackbarMessage } = useAppSnackbar();

    const [selectedPairingId, setSelectedPairingId] = useState<number | null>(null);
    const [searchText, setSearchText] = useState('');
    const [onlineOnly, setOnlineOnly] = useState(false);
    const [step, setStep] = useState<Step>('selectCamera');
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
    const [startTime, setStartTime] = useState<StartTime | undefined>(undefined);
    const [selectedDuration, setSelectedDuration] = useState<number | undefined>(undefined);
    const [durationOptions, setDurationOptions] = useState<IDropdownItem[]>([]);
    const [footageViewState, setFootageViewState] = useState<FootageViewState>('idle');
    const [availableSegments, setAvailableSegments] = useState<CameraDeviceSegment[]>([]);
    const [selectedSegment, setSelectedSegment] = useState<CameraSegmentFullData | undefined>(undefined);
    // Recall start selected on the segment preview step -- an absolute instant driven by the previewed
    // video's paused position or the typed footage-timezone time (unlike `startTime`, which the manual
    // time-and-duration step interprets in the user's timezone).
    const [previewRecallStart, setPreviewRecallStart] = useState<Date | undefined>(undefined);
    // Reported by whichever of TimeAndDurationStep/SegmentPreviewStep is currently mounted (only one ever
    // is) -- true when that step's time input holds a value that's otherwise-valid-shaped but semantically
    // wrong (e.g. a future instant), so Submit should stay disabled without clearing the underlying value.
    const [stepValidationError, setStepValidationError] = useState(false);

    const isRunningStandAlone = props.config?.isRunningStandAlone() ?? false;

    useEffect(() => {
        const fetchMaxDuration = async () => {
            await props.FleetSettings.getFleetSettingsByKeyResults(FleetSettingsKeys.DefaultRecallMax);
            const { getFleetSettingsByKeyResults } = props.FleetSettings.getState();
            const raw = getFleetSettingsByKeyResults.data?.value ? parseInt(getFleetSettingsByKeyResults.data.value) : 0;
            const maxSeconds = raw > 0 ? raw : DEFAULT_DURATION_SECONDS;
            const options = buildDurationOptions(maxSeconds);
            setDurationOptions(options);
            if (selectedDuration === undefined && options.find((o) => o.value === maxSeconds)) {
                setSelectedDuration(maxSeconds);
            }
        };
        fetchMaxDuration();
    }, []);

    useEffect(() => {
        if (props.open && props.preselectedPairingId !== undefined) {
            setSelectedPairingId(props.preselectedPairingId);
            setStep('selectDate');
        }
    }, [props.open, props.preselectedPairingId]);

    const manualStartDate = useMemo(() => {
        if (!startTime || !selectedDate) return undefined;
        let h = startTime.hour;
        if (startTime.amPm === 'PM' && h !== 12) h += 12;
        if (startTime.amPm === 'AM' && h === 12) h = 0;
        return moment(selectedDate).startOf('day').hour(h).minute(startTime.minute).second(startTime.second).toDate();
    }, [selectedDate, startTime]);

    const requestStartDate = step === 'segmentPreview' ? previewRecallStart : manualStartDate;

    const requestEndDate = useMemo(
        () =>
            requestStartDate !== undefined && selectedDuration !== undefined
                ? moment(requestStartDate).add(selectedDuration, 'seconds').toDate()
                : undefined,
        [requestStartDate, selectedDuration],
    );

    const filteredPairings = props.pairings
        .filter((pairing) => {
            const serial = pairing.cameraDevice?.serialNumber ?? '';
            const vehicleName = pairing.vehicle?.name ?? '';
            const lowerSearch = searchText.toLowerCase();

            const matchesSearch =
                !searchText || serial.toLowerCase().includes(lowerSearch) || vehicleName.toLowerCase().includes(lowerSearch);
            const matchesOnlineFilter = !onlineOnly || pairing.cameraDevice?.onlineStatus?.toLowerCase() === 'online';

            return matchesSearch && matchesOnlineFilter;
        })
        .sort((a, b) => (a.vehicle?.name ?? '').localeCompare(b.vehicle?.name ?? ''));

    const deselectIfNotVisible = (search: string, onlineOnlyFilter: boolean) => {
        if (selectedPairingId === null) return;
        const selected = props.pairings.find((p) => p.id === selectedPairingId);
        if (!selected) return;
        const serial = selected.cameraDevice?.serialNumber ?? '';
        const vehicleName = selected.vehicle?.name ?? '';
        const matchesSearch =
            !search || serial.toLowerCase().includes(search.toLowerCase()) || vehicleName.toLowerCase().includes(search.toLowerCase());
        const matchesOnline = !onlineOnlyFilter || selected.cameraDevice?.onlineStatus?.toLowerCase() === 'online';
        if (!matchesSearch || !matchesOnline) {
            setSelectedPairingId(null);
        }
    };

    const handleSearchChange = (newSearch: string) => {
        setSearchText(newSearch);
        deselectIfNotVisible(newSearch, onlineOnly);
    };

    const handleSearchClear = () => {
        setSearchText('');
    };

    const handleOnlineOnlyChange = () => {
        const newOnlineOnly = !onlineOnly;
        setOnlineOnly(newOnlineOnly);
        deselectIfNotVisible(searchText, newOnlineOnly);
    };

    const handleClose = () => {
        setSelectedPairingId(null);
        setSearchText('');
        setOnlineOnly(false);
        setStep('selectCamera');
        setSelectedDate(undefined);
        setStartTime(undefined);
        const maxOption = durationOptions[durationOptions.length - 1];
        setSelectedDuration(maxOption ? (maxOption.value as number) : undefined);
        setFootageViewState('idle');
        setAvailableSegments([]);
        setSelectedSegment(undefined);
        setPreviewRecallStart(undefined);
        setStepValidationError(false);
        props.onClose();
    };

    const handleSubmit = async () => {
        switch (step) {
            case 'selectCamera':
                if (selectedPairingId !== null) {
                    setStep('selectDate');
                }
                break;
            case 'selectDate':
                if (selectedDate !== undefined) {
                    setStep('timeAndDuration');
                }
                break;
            case 'timeAndDuration':
            case 'segmentPreview': {
                if (!selectedPairing || requestStartDate === undefined || requestEndDate === undefined) return;
                let succeeded = true;
                try {
                    await props.videoRecall.requestVideoRecall({
                        integrationPartnerVehicleId: selectedPairing.vehicle?.integrationPartnerVehicleId,
                        eventStartDate: requestStartDate,
                        eventEndDate: requestEndDate,
                    });
                } catch {
                    succeeded = false;
                }
                showSnackbarMessage({
                    message: succeeded ? 'Video recall requested successfully.' : 'Unable to request video recall.',
                    alertType: succeeded ? AlertTypes.Success : AlertTypes.Fail,
                    isEncompass,
                    showIcon: isEncompass,
                });
                handleClose();
                if (succeeded) {
                    // fire-and-forget: a refresh failure here shouldn't surface as an unhandled
                    // rejection after the user has already been told the request succeeded
                    props.onRequestSubmitted?.();
                }
                break;
            }
        }
    };

    const submitButtonDisabled =
        step === 'selectCamera'
            ? selectedPairingId === null
            : step === 'selectDate'
              ? selectedDate === undefined
              : requestStartDate === undefined || requestEndDate === undefined || stepValidationError;

    const handleBack = () => {
        if (step === 'selectDate') {
            setSelectedDate(undefined);
            setStep('selectCamera');
        } else if (step === 'timeAndDuration') {
            setStartTime(undefined);
            const maxOption = durationOptions[durationOptions.length - 1];
            setSelectedDuration(maxOption ? (maxOption.value as number) : undefined);
            setFootageViewState('idle');
            setAvailableSegments([]);
            setStep('selectDate');
        } else if (step === 'segmentPreview') {
            setSelectedSegment(undefined);
            setPreviewRecallStart(undefined);
            setStep('timeAndDuration');
        }
    };

    const canReturnToSelectCamera = props.preselectedPairingId === undefined;
    const onBack =
        (step === 'selectDate' && canReturnToSelectCamera) || step === 'timeAndDuration' || step === 'segmentPreview'
            ? handleBack
            : undefined;

    const handleSegmentSelected = (segment: CameraSegmentFullData) => {
        setSelectedSegment(segment);
        setStep('segmentPreview');
    };

    const handleSegmentNavigate = (segment: CameraSegmentBase) => {
        if (!selectedSegment) return;
        setSelectedSegment({ ...segment, groupSegments: selectedSegment.groupSegments });
    };

    const selectedPairing =
        (step === 'selectDate' || step === 'timeAndDuration' || step === 'segmentPreview') && selectedPairingId !== null
            ? (props.pairings.find((p) => p.id === selectedPairingId) ?? null)
            : null;

    return (
        <SliderDialog
            open={props.open}
            title="Request a Video"
            onClose={handleClose}
            onSubmit={handleSubmit}
            onBack={onBack}
            submitButtonText={step === 'timeAndDuration' || step === 'segmentPreview' ? 'Submit Request' : 'Next'}
            isEncompass={isEncompass}
            isRunningStandAlone={isRunningStandAlone}
            submitButtonDisabled={submitButtonDisabled}
            paperClassName={step === 'segmentPreview' ? styles.drawerPaperWide : styles.drawerPaper}
            headerClassName={styles.drawerHeader}
            footerClassName={styles.drawerFooter}
            contentWrapperClassName={styles.contentWrapper}
            buttonsContainerClassName={styles.footerButtons}
            cancelButtonClassName={styles.cancelButton}
            backButtonClassName={styles.cancelButton}
            submitButtonClassName={styles.nextButton}
        >
            {step === 'selectCamera' && (
                <SelectCameraStep
                    filteredPairings={filteredPairings}
                    selectedPairingId={selectedPairingId}
                    onSelectPairing={setSelectedPairingId}
                    searchText={searchText}
                    onSearchTextChange={handleSearchChange}
                    onSearchClear={handleSearchClear}
                    onlineOnly={onlineOnly}
                    onOnlineOnlyToggle={handleOnlineOnlyChange}
                    isEncompass={isEncompass}
                    styles={styles}
                />
            )}
            {step === 'selectDate' && selectedPairing && (
                <SelectDateStep pairing={selectedPairing} selectedDate={selectedDate} onDateChange={setSelectedDate} />
            )}
            {step === 'timeAndDuration' && selectedPairing && selectedDate && (
                <TimeAndDurationStep
                    pairing={selectedPairing}
                    selectedDate={selectedDate}
                    startTime={startTime}
                    selectedDuration={selectedDuration}
                    durationOptions={durationOptions}
                    footageViewState={footageViewState}
                    availableSegments={availableSegments}
                    onStartTimeChange={setStartTime}
                    onDurationChange={setSelectedDuration}
                    onFootageViewStateChange={setFootageViewState}
                    onAvailableSegmentsChange={setAvailableSegments}
                    onSegmentSelected={handleSegmentSelected}
                    onValidationErrorChange={setStepValidationError}
                />
            )}
            {step === 'segmentPreview' && selectedPairing && selectedDate && selectedSegment && (
                <SegmentPreviewStep
                    pairing={selectedPairing}
                    selectedDate={selectedDate}
                    requestStartDate={requestStartDate}
                    requestEndDate={requestEndDate}
                    selectedSegment={selectedSegment}
                    recallStartDate={previewRecallStart}
                    selectedDuration={selectedDuration}
                    durationOptions={durationOptions}
                    onRecallStartDateChange={setPreviewRecallStart}
                    onDurationChange={setSelectedDuration}
                    onSegmentChange={handleSegmentNavigate}
                    onValidationErrorChange={setStepValidationError}
                />
            )}
        </SliderDialog>
    );
}

export const RequestVideoDrawer = FleetSettingsService.inject(VideoRecallService.inject(_RequestVideoDrawer));
